import { useMemo } from 'react';
import { formatPrice } from '@/lib/utils/format-price';

/** Minimal CT price-snapshot shape the price-display decision reads. */
export interface PriceSnapshotLike {
  activePrice?: { centAmount?: number; currencyCode?: string; fractionDigits?: number } | null;
  inStock?: boolean;
}

/**
 * EWM-525 — the single rule for the "show price vs Unavailable / Out of stock"
 * decision a CT price snapshot drives. Centralised so the PLP tile, PDP and any
 * future surface derive it identically instead of re-inlining
 * `!activePrice` / `typeof inStock !== 'boolean'`.
 */
export const getPriceAvailability = (snapshot?: PriceSnapshotLike | null) => ({
  priceUnavailable: !(!!snapshot?.activePrice && typeof snapshot.activePrice.centAmount === 'number'),
  stockUnavailable: typeof snapshot?.inStock !== 'boolean',
});

export interface PriceDisplay {
  priceUnavailable: boolean;
  stockUnavailable: boolean;
  /** Decimal amount (not cents) when a price is present. */
  amount?: number;
  /** Formatted price string (`$xx.xx`) when a price is present. */
  formatted?: string;
}

/**
 * Hook form — availability flags plus the formatted price for a CT snapshot,
 * using the single storefront formatter (see {@link formatPrice}).
 */
const usePriceDisplay = (snapshot?: PriceSnapshotLike | null): PriceDisplay =>
  useMemo(() => {
    const { priceUnavailable, stockUnavailable } = getPriceAvailability(snapshot);
    const activePrice = snapshot?.activePrice;
    const amount =
      activePrice && typeof activePrice.centAmount === 'number'
        ? activePrice.centAmount / Math.pow(10, activePrice.fractionDigits ?? 2)
        : undefined;
    return {
      priceUnavailable,
      stockUnavailable,
      amount,
      formatted:
        amount !== undefined
          ? formatPrice(amount, activePrice?.currencyCode ?? 'AUD', activePrice?.fractionDigits ?? 2)
          : undefined,
    };
  }, [snapshot]);

export default usePriceDisplay;
